import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import LoadingProgress from "../components/LoadingProgress";
import ErrorMessage from "../components/ErrorMessage";
import {
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  LineChart,
  Line,
} from "recharts";
import {
  fetchPersistedSalesArqueos,
  fetchPersistedOperationalArqueos,
} from "../actions/arqueos";

import "../styles/DashboardView.css";

// ─── Helpers ────────────────────────────────────────────────────
function localIso(d) {
  if (!(d instanceof Date) || isNaN(d)) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

// Locker-type item names tracked by the lambda
const LOCKER_TYPES = [
  "LOCKERBNS",  "LOCKERCNS",  "LOCKERBWS",  "LOCKERCWS",
  "LOCKERBNM",  "LOCKERCNM",  "LOCKERBWM",  "LOCKERCWM",
  "LOCKERBNL",  "LOCKERCNL",  "LOCKERBWL",  "LOCKERCWL",
  "LOCKERBNXL", "LOCKERCNXL", "LOCKERBWXL", "LOCKERCWXL",
  "LOCKERBN2XL","LOCKERCN2XL","LOCKERBW2XL","LOCKERCW2XL",
  "LOCKERBN3XL","LOCKERCN3XL","LOCKERBW3XL","LOCKERCW3XL",
  "TUMBONAS",   "FLOTADORES", "FLOTADORESHALFDAY", "FASTPASS",
];

// Extra products that have no locker capacity / occupancy meaning
const EXTRA_TYPES = ["TUMBONAS", "FLOTADORES", "FLOTADORESHALFDAY", "FASTPASS"];

// Locker types used for occupancy calculations (excludes extras)
const OCCUPANCY_TYPES = LOCKER_TYPES.filter(k => !EXTRA_TYPES.includes(k));

// Friendly display labels for locker type keys
function lockerTypeLabel(key) {
  const map = {
    // Standard
    LOCKERBNS:   "Standard",
    LOCKERCNS:   "Standard Carga",
    LOCKERBWS:   "Standard XL",
    LOCKERCWS:   "Standard Carga XL",
    // Medium
    LOCKERBNM:   "Medium",
    LOCKERCNM:   "Medium Carga",
    LOCKERBWM:   "Medium XL",
    LOCKERCWM:   "Medium Carga XL",
    // Large
    LOCKERBNL:   "Large",
    LOCKERCNL:   "Large Carga",
    LOCKERBWL:   "Large XL",
    LOCKERCWL:   "Large Carga XL",
    // Large 4
    LOCKERBNXL:  "Large 4",
    LOCKERCNXL:  "Large 4 Carga",
    LOCKERBWXL:  "Large 4 XL",
    LOCKERCWXL:  "Large 4 Carga XL",
    // Large 5
    LOCKERBN2XL: "Large 5",
    LOCKERCN2XL: "Large 5 Carga",
    LOCKERBW2XL: "Large 5 XL",
    LOCKERCW2XL: "Large 5 Carga XL",
    // Ski
    LOCKERBN3XL: "Ski",
    LOCKERCN3XL: "Ski Carga",
    LOCKERBW3XL: "Ski XL",
    LOCKERCW3XL: "Ski Carga XL",
    // Extras
    TUMBONAS:           "Tumbonas",
    FLOTADORES:         "Flotadores",
    FLOTADORESHALFDAY:  "Flotadores ½ día",
    FASTPASS:           "FastPass",
  };
  return map[key] || key;
}

// Aggregate locker types into size groups
// B = Buy/normal, C = Carga (charging), N = Normal, W = Winter/XL variant
// Both B and C are sales — group by size suffix
function sizeGroup(key) {
  // all LOCKER* keys: strip prefix and first two chars (variant code e.g. BN, CW, BW, CN)
  if (key.startsWith("LOCKER")) {
    const rest = key.replace(/^LOCKER[BC][NW]/, "");
    // rest is the size: S, M, L, XL, 2XL, 3XL
    const nameMap = { S: "Standard", M: "Medium", L: "Large", XL: "Large 4", "2XL": "Large 5", "3XL": "Ski" };
    return nameMap[rest] || rest || null;
  }
  if (key === "TUMBONAS") return "Tumbonas";
  if (key === "FLOTADORES" || key === "FLOTADORESHALFDAY") return "Flotadores";
  if (key === "FASTPASS") return "FastPass";
  return null;
}

// Chart colors palette
const COLORS = [
  "#2563eb", "#7c3aed", "#059669", "#d97706", "#dc2626",
  "#0891b2", "#be185d", "#4f46e5", "#15803d", "#ca8a04",
  "#9333ea", "#e11d48",
];

// ─── Component ──────────────────────────────────────────────────
class OccupancyDashboardView extends React.Component {
  constructor(props) {
    super(props);
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const monthStart = new Date(year, month - 1, 1);
    const monthEnd = new Date(year, month, 0);

    const lyStart = new Date(year - 1, month - 1, 1);
    const lyEnd = new Date(year - 1, month, 0);

    this.state = {
      startDate: localIso(monthStart),
      endDate: localIso(monthEnd),
      periodType: "range",
      tempPeriodType: "range",
      tempStartDate: localIso(monthStart),
      tempEndDate: localIso(monthEnd),
      compareStartDate: localIso(lyStart),
      compareEndDate: localIso(lyEnd),
      tempCompareStartDate: localIso(lyStart),
      tempCompareEndDate: localIso(lyEnd),
      loading: false,
      country: "",
      businessModel: "",
      vertical: "",
      activeOnly: false,
      siteFilter: "",
      lockerTypeFilter: "",
      showAdvancedFilters: false,
      excludeFaults: false,
      excludeNotConfigured: false,
      lySalesArqueos: null,
      lyOperationalArqueos: null,
      viewMode: "size",
      chartType: "trend",
      occupancyMode: "type",
      hiddenLines: {},
    };
    // uncontrolled ref + debounce timer for customer filter input
    this.siteInputRef = React.createRef();
    this._siteFilterTimer = null;
  }

  componentDidMount() {
    this.load();
  }

  // Helper: test whether a row matches free-text filter
  rowMatchesFilter(row, q) {
    if (!row || !q) return false;
    const ql = q.toString().toLowerCase();
    const candidates = [
      row.CustomerName, row.Customer, row.CustomerId, row.Fullname,
      row.Site, row.Location, row.SiteName, row.MachineSerial, row.MachineId, row.Serial,
    ];
    return candidates.some(c => {
      try { return c && c.toString().toLowerCase().includes(ql); } catch (e) { return false; }
    });
  }

  fillLastYear = () => {
    const { tempPeriodType, tempStartDate, tempEndDate } = this.state;
    if (!tempStartDate) return;
    const shiftYear = (iso, delta) => {
      const d = new Date(iso);
      if (isNaN(d)) return "";
      d.setFullYear(d.getFullYear() + delta);
      return localIso(d);
    };
    let compStart = "", compEnd = "";
    switch (tempPeriodType) {
      case "range":
        compStart = shiftYear(tempStartDate, -1);
        compEnd = tempEndDate ? shiftYear(tempEndDate, -1) : "";
        break;
      case "day":
        compStart = shiftYear(tempStartDate, -1);
        break;
      case "month": {
        const [y, m] = tempStartDate.split("-");
        if (y && m) compStart = `${String(Number(y) - 1).padStart(4, "0")}-${m}`;
        break;
      }
      case "year": {
        const yy = tempStartDate;
        if (yy) compStart = String(Number(yy) - 1);
        break;
      }
      default: break;
    }
    this.setState({ tempCompareStartDate: compStart, tempCompareEndDate: compEnd });
  };

  load = async () => {
    const { startDate, endDate, compareStartDate, compareEndDate, country, businessModel } = this.state;
    const makeBm = (bm) => {
      if (!bm) return undefined;
      const map = { revenue_share: "REVENUE SHARE", alquiler: "RENTAL", venta: "SALE" };
      return map[bm] || bm;
    };
    const bmParam = makeBm(businessModel);
    this.setState({ loading: true });

    try {
      // LY data
      let lySalesAction = { response: [] };
      let lyOpAction = { response: [] };
      if (compareStartDate && compareEndDate) {
        const lyStartISO = localIso(new Date(compareStartDate));
        const lyEndISO = localIso(new Date(compareEndDate));
        lySalesAction = await this.props.fetchPersistedSalesArqueos(lyStartISO, lyEndISO, bmParam, undefined, country || undefined);
        lyOpAction = await this.props.fetchPersistedOperationalArqueos(lyStartISO, lyEndISO, undefined, undefined, country || undefined);
      }

      const EXCLUDE_CID = "620d1664db950220fabc0a41";
      const excludeRow = r => ((r.CustomerId || r.Customer || "") + "") !== EXCLUDE_CID;
      this.setState({
        lySalesArqueos: ((lySalesAction && lySalesAction.response) || []).filter(excludeRow),
        lyOperationalArqueos: ((lyOpAction && lyOpAction.response) || []).filter(excludeRow),
      });

      // Current period — fetch sequentially to avoid Redux race conditions
      await this.props.fetchPersistedSalesArqueos(startDate, endDate, bmParam, undefined, country || undefined);
      await this.props.fetchPersistedOperationalArqueos(startDate, endDate, undefined, undefined, country || undefined);

      this.setState({ loading: false });
    } catch (err) {
      this.setState({ loading: false });
    }
  };

  // ─── Filtering helpers (same logic as OperationalDashboardView) ───
  filterRows(rows, applyActiveFilter = true) {
    const { startDate, endDate, businessModel, country, vertical, siteFilter, activeOnly } = this.state;
    const EXCLUDE_CID = "620d1664db950220fabc0a41";
    let arr = (rows || []).slice();

    // exclude VACWAY TECH
    arr = arr.filter(r => ((r.CustomerId || r.Customer || "") + "") !== EXCLUDE_CID);

    // date range
    if (startDate || endDate) {
      arr = arr.filter(r => {
        const day = r.Day ? (r.Day.slice ? r.Day.slice(0, 10) : String(r.Day).slice(0, 10)) : null;
        if (!day) return false;
        if (startDate && day < startDate) return false;
        if (endDate && day > endDate) return false;
        return true;
      });
    }

    // businessModel
    if (businessModel) {
      const map = { revenue_share: "REVENUE SHARE", alquiler: "RENTAL", venta: "SALE" };
      const bm = map[businessModel] || businessModel;
      arr = arr.filter(r => (r.BusinessModel || r.ContractType || "") === bm);
    }

    // country
    if (country) {
      arr = arr.filter(r => (r.CustomerCountry || r.Country || r.SiteCountry || "") === country);
    }

    // vertical
    if (vertical) {
      arr = arr.filter(r => (r.CustomerCategory || r.Category || r.Vertical || "") === vertical);
    }

    // siteFilter
    if (siteFilter) {
      arr = arr.filter(r => this.rowMatchesFilter(r, siteFilter));
    }

    // activeOnly — only applied to sales rows
    if (activeOnly && applyActiveFilter) {
      arr = arr.filter(r => {
        if (r.Status) {
          const st = r.Status.toString().toLowerCase();
          return st === "active" || st === "operative";
        }
        return true; // rows without Status pass through
      });
    }

    return arr;
  }

  // ─── Compute occupation data ──────────────────────────────────
  computeOccupationByType(salesRows, opRows) {
    // salesRows already filtered — aggregate ItemsCounts and ItemsPrices per locker type
    const counts = {};
    const revenue = {};
    salesRows.forEach(row => {
      const ic = row.ItemsCounts || {};
      const ip = row.ItemsPrices || {};
      for (const key of LOCKER_TYPES) {
        if (ic[key]) {
          counts[key] = (counts[key] || 0) + ic[key];
        }
        if (ip[key]) {
          revenue[key] = (revenue[key] || 0) + ip[key];
        }
      }
    });

    // total lockers from operational data
    let totalLockers = 0;
    opRows.forEach(r => {
      totalLockers += (r.TotlockerStatus && r.TotlockerStatus.total) || r.TotalLockers || 0;
    });

    return { counts, revenue, totalLockers };
  }

  computeOccupationBySizeGroup(salesRows, opRows) {
    const { counts, revenue, totalLockers } = this.computeOccupationByType(salesRows, opRows);
    const grouped = {};
    for (const key of LOCKER_TYPES) {
      const grp = sizeGroup(key);
      if (!grp) continue; // skip cancellations
      if (!grouped[grp]) grouped[grp] = { count: 0, revenue: 0 };
      grouped[grp].count += (counts[key] || 0);
      grouped[grp].revenue += (revenue[key] || 0);
    }
    return { grouped, totalLockers };
  }

  // ─── Compute daily occupation % ─────────────────────────────
  // For each day+type: sales_count[key] / available_capacity[key] * 100
  // available_capacity = ItemsCounts[key] - ItemsCountsFault[key] - ItemsCountsNotConfigured[key]
  computeDailyOccupation(salesRows, opRows, excludeFaults = true, excludeNotConfigured = true) {
    // ─ aggregate sales counts by day+key
    const salesByDay = {};
    salesRows.forEach(row => {
      const day = row.Day ? (row.Day.slice ? row.Day.slice(0, 10) : String(row.Day).slice(0, 10)) : null;
      if (!day) return;
      if (!salesByDay[day]) salesByDay[day] = {};
      const ic = row.ItemsCounts || {};
      for (const key of OCCUPANCY_TYPES) {
        if (ic[key]) salesByDay[day][key] = (salesByDay[day][key] || 0) + ic[key];
      }
    });

    // ─ aggregate available capacity by day+key from operational rows
    const capacityByDay = {};
    opRows.forEach(row => {
      const day = row.Day ? (row.Day.slice ? row.Day.slice(0, 10) : String(row.Day).slice(0, 10)) : null;
      if (!day) return;
      if (!capacityByDay[day]) capacityByDay[day] = {};
      const total = row.ItemsCounts || {};
      const fault = row.ItemsCountsFault || {};
      const notConf = row.ItemsCountsNotConfigured || {};
      for (const key of OCCUPANCY_TYPES) {
        const faultVal = excludeFaults ? (fault[key] || 0) : 0;
        const notConfVal = excludeNotConfigured ? (notConf[key] || 0) : 0;
        const avail = Math.max(0, (total[key] || 0) - faultVal - notConfVal);
        if (avail > 0) capacityByDay[day][key] = (capacityByDay[day][key] || 0) + avail;
      }
    });

    const allDays = [...new Set([...Object.keys(salesByDay), ...Object.keys(capacityByDay)])].sort();
    return allDays.map(day => {
      const entry = { day: day.slice(5) }; // MM-DD
      const sales = salesByDay[day] || {};
      const capacity = capacityByDay[day] || {};
      const allKeys = [...new Set([...Object.keys(sales), ...Object.keys(capacity)])];
      for (const key of allKeys) {
        const s = sales[key] || 0;
        const c = capacity[key] || 0;
        // when capacity available → occupancy %; otherwise fall back to raw sales count
        entry[key] = c > 0 ? Number(((s / c) * 100).toFixed(1)) : (s > 0 ? s : null);
        // store raw values for tooltip
        entry[`${key}__s`] = s;
        entry[`${key}__c`] = c;
      }
      return entry;
    });
  }

  // ─── Compute daily occupation TOTAL (single aggregated line) ──
  computeDailyOccupationTotal(salesRows, opRows, excludeFaults = true, excludeNotConfigured = true) {
    const salesByDay = {};
    salesRows.forEach(row => {
      const day = row.Day ? (row.Day.slice ? row.Day.slice(0, 10) : String(row.Day).slice(0, 10)) : null;
      if (!day) return;
      if (!salesByDay[day]) salesByDay[day] = 0;
      const ic = row.ItemsCounts || {};
      for (const key of OCCUPANCY_TYPES) {
        if (ic[key]) salesByDay[day] += ic[key];
      }
    });

    const capacityByDay = {};
    opRows.forEach(row => {
      const day = row.Day ? (row.Day.slice ? row.Day.slice(0, 10) : String(row.Day).slice(0, 10)) : null;
      if (!day) return;
      if (!capacityByDay[day]) capacityByDay[day] = 0;
      const total = row.ItemsCounts || {};
      const fault = row.ItemsCountsFault || {};
      const notConf = row.ItemsCountsNotConfigured || {};
      for (const key of OCCUPANCY_TYPES) {
        const faultVal = excludeFaults ? (fault[key] || 0) : 0;
        const notConfVal = excludeNotConfigured ? (notConf[key] || 0) : 0;
        const avail = Math.max(0, (total[key] || 0) - faultVal - notConfVal);
        if (avail > 0) capacityByDay[day] += avail;
      }
    });

    const allDays = [...new Set([...Object.keys(salesByDay), ...Object.keys(capacityByDay)])].sort();
    return allDays.map(day => {
      const s = salesByDay[day] || 0;
      const c = capacityByDay[day] || 0;
      return {
        day: day.slice(5),
        Total: c > 0 ? Number(((s / c) * 100).toFixed(1)) : (s > 0 ? s : null),
        Total__s: s,
        Total__c: c,
      };
    });
  }

  // ─── Render ────────────────────────────────────────────────────
  render() {
    const { loading, startDate, endDate, compareStartDate, compareEndDate,
      country, businessModel, vertical, siteFilter, activeOnly, lockerTypeFilter,
      showAdvancedFilters, excludeFaults, excludeNotConfigured, hiddenLines } = this.state;

    const toggleLine = (e) => {
      const key = e.dataKey;
      this.setState(prev => ({ hiddenLines: { ...prev.hiddenLines, [key]: !prev.hiddenLines[key] } }));
    };
    const { role, generatedSalesArqueos, generatedOperationalArqueos } = this.props;

    const canLoad = !!this.state.tempStartDate;

    // period labels
    const periodTitle = startDate && endDate ? `${startDate} → ${endDate}` : "—";
    const compareTitle = compareStartDate && compareEndDate ? `${compareStartDate} → ${compareEndDate}` : "desactivada";

    // ─ current period filtered data
    const salesRows = this.filterRows(generatedSalesArqueos || []);
    const opRows = this.filterRows(generatedOperationalArqueos || [], false);

    // ─ LY filtered data (apply same filters but against LY date range)
    const lySalesRows = (() => {
      if (!this.state.lySalesArqueos) return [];
      const { businessModel: bm, country: c, vertical: v, siteFilter: sf } = this.state;
      const EXCLUDE_CID = "620d1664db950220fabc0a41";
      let arr = this.state.lySalesArqueos.slice();
      arr = arr.filter(r => ((r.CustomerId || r.Customer || "") + "") !== EXCLUDE_CID);
      if (bm) {
        const map = { revenue_share: "REVENUE SHARE", alquiler: "RENTAL", venta: "SALE" };
        const bmVal = map[bm] || bm;
        arr = arr.filter(r => (r.BusinessModel || r.ContractType || "") === bmVal);
      }
      if (c) arr = arr.filter(r => (r.CustomerCountry || r.Country || r.SiteCountry || "") === c);
      if (v) arr = arr.filter(r => (r.CustomerCategory || r.Category || r.Vertical || "") === v);
      if (sf) arr = arr.filter(r => this.rowMatchesFilter(r, sf));
      return arr;
    })();

    // ─ compute occupation breakdown (type-level for table; size-group not needed for chart)
    const { counts: currentCounts, revenue: currentRevenue } = this.computeOccupationByType(salesRows, opRows);
    const { counts: lyCounts } = this.computeOccupationByType(lySalesRows, []);

    const hasLY = lySalesRows.length > 0;

    // Determine whether the locker filter is a specific key or a size group
    const lockerFilterIsKey = lockerTypeFilter && LOCKER_TYPES.includes(lockerTypeFilter);
    const lockerFilterGroup = lockerFilterIsKey ? sizeGroup(lockerTypeFilter) : lockerTypeFilter;

    // ─ types used in the breakdown table
    const usedTypes = LOCKER_TYPES.filter(k =>
      (!lockerTypeFilter || (lockerFilterIsKey ? k === lockerTypeFilter : sizeGroup(k) === lockerFilterGroup)) &&
      ((currentCounts[k] || 0) > 0 || (lyCounts[k] || 0) > 0)
    );

    // ─ daily trend data (occupancy %)
    const dailyData = this.computeDailyOccupation(salesRows, opRows, excludeFaults, excludeNotConfigured);
    const trendGroups = [...new Set(dailyData.flatMap(d => Object.keys(d).filter(k => k !== "day" && !k.endsWith("__s") && !k.endsWith("__c"))))]
      .filter(k =>
        (!lockerTypeFilter || (lockerFilterIsKey ? k === lockerTypeFilter : sizeGroup(k) === lockerFilterGroup)) &&
        dailyData.some(d => d[k] != null && d[k] > 0)
      );

    // ─ total daily data (aggregated single line)
    const totalDailyData = this.computeDailyOccupationTotal(salesRows, opRows, excludeFaults, excludeNotConfigured);

    // ─ LY operational rows (filtered, no date-range filter — already in LY range)
    const lyOpRows = (() => {
      if (!this.state.lyOperationalArqueos) return [];
      const { businessModel: bm, country: c, vertical: v, siteFilter: sf } = this.state;
      const EXCLUDE_CID = "620d1664db950220fabc0a41";
      let arr = this.state.lyOperationalArqueos.slice();
      arr = arr.filter(r => ((r.CustomerId || r.Customer || "") + "") !== EXCLUDE_CID);
      if (bm) {
        const map = { revenue_share: "REVENUE SHARE", alquiler: "RENTAL", venta: "SALE" };
        arr = arr.filter(r => (r.BusinessModel || r.ContractType || "") === (map[bm] || bm));
      }
      if (c) arr = arr.filter(r => (r.CustomerCountry || r.Country || r.SiteCountry || "") === c);
      if (v) arr = arr.filter(r => (r.CustomerCategory || r.Category || r.Vertical || "") === v);
      if (sf) arr = arr.filter(r => this.rowMatchesFilter(r, sf));
      return arr;
    })();

    // ─ LY daily data (MM-DD aligned, used for comparison overlay)
    const lyDailyData = hasLY ? this.computeDailyOccupation(lySalesRows, lyOpRows, excludeFaults, excludeNotConfigured) : [];
    const lyTotalDailyData = hasLY ? this.computeDailyOccupationTotal(lySalesRows, lyOpRows, excludeFaults, excludeNotConfigured) : [];

    // ─ merge current + LY by MM-DD (LY keys prefixed with LY_)
    const mergedDailyData = (() => {
      const lyMap = {};
      lyDailyData.forEach(d => { lyMap[d.day] = d; });
      const allDays = [...new Set([...dailyData.map(d => d.day), ...lyDailyData.map(d => d.day)])].sort();
      return allDays.map(day => {
        const cur = dailyData.find(d => d.day === day) || { day };
        const ly = lyMap[day] || {};
        const merged = { ...cur };
        for (const [k, v] of Object.entries(ly)) {
          if (k !== "day") merged[`LY_${k}`] = v;
        }
        return merged;
      });
    })();

    const mergedTotalDailyData = (() => {
      const lyMap = {};
      lyTotalDailyData.forEach(d => { lyMap[d.day] = d; });
      const allDays = [...new Set([...totalDailyData.map(d => d.day), ...lyTotalDailyData.map(d => d.day)])].sort();
      return allDays.map(day => {
        const cur = totalDailyData.find(d => d.day === day) || { day };
        const ly = lyMap[day] || {};
        return { ...cur, LY_Total: ly.Total ?? null, LY_Total__s: ly.Total__s ?? null, LY_Total__c: ly.Total__c ?? null };
      });
    })();

    // LY series present in merged type data
    const lyTrendGroups = hasLY ? trendGroups.filter(k => mergedDailyData.some(d => d[`LY_${k}`] != null && d[`LY_${k}`] > 0)) : [];

    // detect whether operational arqueos include ItemsCounts (post-lambda-update data)
    const hasCapacityData = opRows.some(r => r.ItemsCounts && Object.keys(r.ItemsCounts).length > 0);

    // ─ totals (used by table only)
    const totalSales = Object.entries(currentCounts)
      .filter(([k]) => !lockerTypeFilter || (lockerFilterIsKey ? k === lockerTypeFilter : sizeGroup(k) === lockerFilterGroup))
      .reduce((s, [, v]) => s + v, 0);

    // customer options for filter datalist
    const customerOptions = [...new Set((generatedSalesArqueos || []).map(r => r.CustomerName).filter(Boolean))].sort();
    // country options
    const countryOptions = [...new Set([
      ...(generatedSalesArqueos || []).map(r => r.CustomerCountry || r.Country).filter(Boolean),
      ...(generatedOperationalArqueos || []).map(r => r.Country).filter(Boolean),
    ])].sort();
    // vertical options
    const verticalOptions = [...new Set([
      ...(generatedSalesArqueos || []).map(r => r.CustomerCategory || r.Category || r.Vertical).filter(Boolean),
      ...(generatedOperationalArqueos || []).map(r => r.Category).filter(Boolean),
    ])].sort();

    if (role !== "ADMIN") return <ErrorMessage message="Not authorized" />;

    return (
      <div className="dashboard-view">
        {/* ─── Header ──────────────────────────── */}
        <div
          className="dashboard-header"
          style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <div className="dashboard-title-section">
            <h1 className="dashboard-title">
              <i className="fas fa-chart-bar"></i> Dashboard Ocupación
            </h1>
            <div className="small text-muted mt-1">
              Período: {periodTitle} (vs {compareTitle})
            </div>
          </div>

          <div className="dashboard-actions">
            <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
              <input
                type="text"
                list="occ-customer-options"
                className="dashboard-filter-input"
                placeholder="Filtrar por cliente"
                ref={this.siteInputRef}
                defaultValue=""
                onChange={e => {
                  const val = e.target.value;
                  clearTimeout(this._siteFilterTimer);
                  this._siteFilterTimer = setTimeout(() => {
                    this.setState({ siteFilter: val });
                  }, 350);
                }}
                onBlur={e => {
                  clearTimeout(this._siteFilterTimer);
                  const val = e.target.value;
                  if (val === "" || customerOptions.includes(val)) {
                    this.setState({ siteFilter: val });
                  } else {
                    // revert displayed text to last committed value
                    if (this.siteInputRef.current) {
                      this.siteInputRef.current.value = this.state.siteFilter;
                    }
                  }
                }}
                style={{ width: 260 }}
              />
              <datalist id="occ-customer-options">
                {customerOptions.map(c => <option key={c} value={c} />)}
              </datalist>

              <select
                className="dashboard-filter-input"
                value={country}
                onChange={e => this.setState({ country: e.target.value })}
              >
                <option value="">País (todos)</option>
                {countryOptions.map(c => <option key={c} value={c}>{c}</option>)}
              </select>

              <select
                className="dashboard-filter-input"
                value={vertical}
                onChange={e => this.setState({ vertical: e.target.value })}
              >
                <option value="">Vertical (todos)</option>
                {verticalOptions.map(v => <option key={v} value={v}>{v}</option>)}
              </select>

              <select
                className="dashboard-filter-input"
                value={businessModel}
                onChange={e => this.setState({ businessModel: e.target.value })}
              >
                <option value="">Modelo negocio (todos)</option>
                <option value="revenue_share">Revenue-share</option>
                <option value="alquiler">Alquiler (Rental)</option>
                <option value="venta">Venta (Sale)</option>
              </select>

              <select
                className="dashboard-filter-input"
                value={lockerTypeFilter}
                onChange={e => this.setState({ lockerTypeFilter: e.target.value })}
              >
                <option value="">Tipo locker (todos)</option>
                <optgroup label="Standard">
                  <option value="LOCKERBNS">Standard</option>
                  <option value="LOCKERCNS">Standard Carga</option>
                  <option value="LOCKERBWS">Standard XL</option>
                  <option value="LOCKERCWS">Standard Carga XL</option>
                </optgroup>
                <optgroup label="Medium">
                  <option value="LOCKERBNM">Medium</option>
                  <option value="LOCKERCNM">Medium Carga</option>
                  <option value="LOCKERBWM">Medium XL</option>
                  <option value="LOCKERCWM">Medium Carga XL</option>
                </optgroup>
                <optgroup label="Large">
                  <option value="LOCKERBNL">Large</option>
                  <option value="LOCKERCNL">Large Carga</option>
                  <option value="LOCKERBWL">Large XL</option>
                  <option value="LOCKERCWL">Large Carga XL</option>
                </optgroup>
                <optgroup label="Large 4">
                  <option value="LOCKERBNXL">Large 4</option>
                  <option value="LOCKERCNXL">Large 4 Carga</option>
                  <option value="LOCKERBWXL">Large 4 XL</option>
                  <option value="LOCKERCWXL">Large 4 Carga XL</option>
                </optgroup>
                <optgroup label="Large 5">
                  <option value="LOCKERBN2XL">Large 5</option>
                  <option value="LOCKERCN2XL">Large 5 Carga</option>
                  <option value="LOCKERBW2XL">Large 5 XL</option>
                  <option value="LOCKERCW2XL">Large 5 Carga XL</option>
                </optgroup>
                <optgroup label="Ski">
                  <option value="LOCKERBN3XL">Ski</option>
                  <option value="LOCKERCN3XL">Ski Carga</option>
                  <option value="LOCKERBW3XL">Ski XL</option>
                  <option value="LOCKERCW3XL">Ski Carga XL</option>
                </optgroup>
                <optgroup label="Extras">
                  <option value="TUMBONAS">Tumbonas</option>
                  <option value="FLOTADORES">Flotadores</option>
                  <option value="FLOTADORESHALFDAY">Flotadores ½ día</option>
                  <option value="FASTPASS">FastPass</option>
                </optgroup>
              </select>

              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <label className="small text-muted" style={{ margin: 0 }}>
                  Solo ubicaciones activas
                </label>
                <label className="dashboard-switch" style={{ margin: 0 }}>
                  <input
                    type="checkbox"
                    checked={activeOnly}
                    onChange={e => this.setState({ activeOnly: e.target.checked })}
                  />
                  <span className="slider round" />
                </label>
              </div>
            </div>

            {/* toggle button */}
            <button
              type="button"
              className="dashboard-filter-input toggle-filters"
              onClick={() => this.setState({ showAdvancedFilters: !showAdvancedFilters })}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}
            >
              {showAdvancedFilters ? (
                <>
                  <i className="fas fa-chevron-up" style={{ marginRight: 6 }}></i>
                  Ocultar filtros
                </>
              ) : (
                <>
                  <i className="fas fa-chevron-down" style={{ marginRight: 6 }}></i>
                  Mostrar filtros
                </>
              )}
            </button>
          </div>
        </div>

        {/* ─── Collapsible date / period panel ── */}
        {showAdvancedFilters && (
          <div
            className="dashboard-header mb-3"
            style={{ padding: "12px 20px", justifyContent: "center" }}
          >
            <div
              className="d-flex align-items-center flex-wrap justify-content-center"
              style={{ gap: 8 }}
            >
              <select
                className="filter-input"
                value={this.state.tempPeriodType}
                onChange={e => this.setState({
                  tempPeriodType: e.target.value,
                  tempStartDate: "",
                  tempEndDate: "",
                  tempCompareStartDate: "",
                  tempCompareEndDate: "",
                })}
                style={{ width: 140 }}
              >
                <option value="range">Rango</option>
                <option value="day">Día</option>
                <option value="month">Mes</option>
                <option value="year">Año</option>
              </select>

              {this.state.tempPeriodType === "range" && (
                <>
                  <input type="date" className="filter-input" title="Origen inicio" value={this.state.tempStartDate} onChange={e => this.setState({ tempStartDate: e.target.value })} style={{ width: 140 }} />
                  <input type="date" className="filter-input" title="Origen fin" value={this.state.tempEndDate} onChange={e => this.setState({ tempEndDate: e.target.value })} style={{ width: 140 }} />
                </>
              )}
              {this.state.tempPeriodType === "day" && (
                <input type="date" className="filter-input" title="Origen (día)" value={this.state.tempStartDate} onChange={e => this.setState({ tempStartDate: e.target.value })} style={{ width: 140 }} />
              )}
              {this.state.tempPeriodType === "month" && (
                <input type="month" className="filter-input" title="Origen (mes)" value={this.state.tempStartDate} onChange={e => this.setState({ tempStartDate: e.target.value })} style={{ width: 140 }} />
              )}
              {this.state.tempPeriodType === "year" && (
                <input type="number" min="1900" max="2100" className="filter-input" title="Origen (año)" value={this.state.tempStartDate} onChange={e => this.setState({ tempStartDate: e.target.value })} style={{ width: 80 }} />
              )}

              <span className="small text-muted" style={{ display: "flex", alignItems: "center", gap: 4 }}>
                comp. <em>(leave blank to disable)</em>
                <button
                  className="btn btn-sm btn-secondary"
                  style={{ padding: "0 8px", fontSize: "0.8rem", height: "36px", lineHeight: "36px" }}
                  title="Autofill comparación mismo período año anterior"
                  onClick={this.fillLastYear}
                  disabled={!this.state.tempStartDate}
                >
                  Año ant.
                </button>
              </span>

              {this.state.tempPeriodType === "range" && (
                <>
                  <input type="date" className="filter-input" title="Comparación inicio (dejar vacío para desactivar)" value={this.state.tempCompareStartDate} onChange={e => this.setState({ tempCompareStartDate: e.target.value })} style={{ width: 140 }} />
                  <input type="date" className="filter-input" title="Comparación fin (dejar vacío para desactivar)" value={this.state.tempCompareEndDate} onChange={e => this.setState({ tempCompareEndDate: e.target.value })} style={{ width: 140 }} />
                </>
              )}
              {this.state.tempPeriodType === "day" && (
                <input type="date" className="filter-input" title="Comparación (día)" value={this.state.tempCompareStartDate} onChange={e => this.setState({ tempCompareStartDate: e.target.value })} style={{ width: 140 }} />
              )}
              {this.state.tempPeriodType === "month" && (
                <input type="month" className="filter-input" title="Comparación (mes)" value={this.state.tempCompareStartDate} onChange={e => this.setState({ tempCompareStartDate: e.target.value })} style={{ width: 140 }} />
              )}
              {this.state.tempPeriodType === "year" && (
                <input type="number" min="1900" max="2100" className="filter-input" title="Comparación (año)" value={this.state.tempCompareStartDate} onChange={e => this.setState({ tempCompareStartDate: e.target.value })} style={{ width: 80 }} />
              )}

              <button
                className="btn btn-vw ml-2"
                onClick={() => {
                  const { tempPeriodType, tempStartDate, tempEndDate, tempCompareStartDate, tempCompareEndDate } = this.state;
                  const normalize = (type, val1, val2) => {
                    if (!val1) return { s: "", e: "" };
                    let s = "", e = "";
                    switch (type) {
                      case "range": s = val1; e = val2; break;
                      case "day": s = val1; e = val1; break;
                      case "month": {
                        const [y, m] = val1.split("-");
                        if (y && m) { s = `${y}-${m}-01`; e = localIso(new Date(y, m, 0)); }
                        break;
                      }
                      case "year": {
                        if (val1) { s = `${val1}-01-01`; e = `${val1}-12-31`; }
                        break;
                      }
                      default: break;
                    }
                    return { s, e };
                  };
                  const { s: sd, e: ed } = normalize(tempPeriodType, tempStartDate, tempEndDate);
                  const { s: csd, e: ced } = normalize(tempPeriodType, tempCompareStartDate, tempCompareEndDate);
                  // use setState callback to guarantee state is flushed before load() reads it
                  this.setState(
                    { startDate: sd, endDate: ed, compareStartDate: csd, compareEndDate: ced, periodType: tempPeriodType },
                    () => this.load()
                  );
                }}
                disabled={loading || !canLoad}
              >
                {loading ? "Loading..." : "Cargar"}
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <LoadingProgress />
        ) : (
          <div className="dashboard-kpi-wrapper pb-3">



            {/* ─── Main Chart ─────────────────────── */}
            <div style={{ background: "var(--card-bg, #fff)", borderRadius: 12, padding: 20, boxShadow: "0 1px 3px rgba(0,0,0,.08)", marginBottom: 24 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <h3 style={{ fontSize: "1rem", fontWeight: 600, margin: 0 }}>
                  {this.state.occupancyMode === "total" ? "Tendencia Diaria de Ocupación Total" : "Tendencia Diaria de Ocupación por Tipo"}
                  {!hasCapacityData && <span style={{ fontSize: "0.8rem", fontWeight: 400, color: "#f59e0b", marginLeft: 8 }}>(sin datos de capacidad — mostrando uds. vendidas)</span>}
                </h3>
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  {/* Exclude faults toggle */}
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.8rem", color: "#374151", cursor: "pointer", margin: 0 }}>
                    <input
                      type="checkbox"
                      checked={excludeFaults}
                      onChange={e => this.setState({ excludeFaults: e.target.checked })}
                      style={{ width: 14, height: 14, cursor: "pointer" }}
                    />
                    Con averías
                  </label>
                  {/* Exclude not configured toggle */}
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.8rem", color: "#374151", cursor: "pointer", margin: 0 }}>
                    <input
                      type="checkbox"
                      checked={excludeNotConfigured}
                      onChange={e => this.setState({ excludeNotConfigured: e.target.checked })}
                      style={{ width: 14, height: 14, cursor: "pointer" }}
                    />
                    Con no configurados
                  </label>
                  {/* Por Tipo / Total selector */}
                  <div style={{ display: "inline-flex", borderRadius: 6, overflow: "hidden", border: "1px solid #d1d5db" }}>
                    {[{ key: "type", label: "Por Tipo" }, { key: "total", label: "Total" }].map(opt => (
                      <button
                        key={opt.key}
                        onClick={() => this.setState({ occupancyMode: opt.key })}
                        style={{
                          padding: "4px 14px", fontSize: "0.8rem", fontWeight: 500, cursor: "pointer",
                          border: "none", outline: "none",
                          background: this.state.occupancyMode === opt.key ? "#2563eb" : "#fff",
                          color: this.state.occupancyMode === opt.key ? "#fff" : "#374151",
                          transition: "background .15s, color .15s",
                        }}
                      >{opt.label}</button>
                    ))}
                  </div>
                </div>
              </div>

              <ResponsiveContainer width="100%" height={400}>
                {this.state.occupancyMode === "total" ? (
                  <LineChart data={mergedTotalDailyData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={v => hasCapacityData ? `${v}%` : v} domain={[0, "auto"]} />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (!active || !payload || !payload.length) return null;
                        const row = payload[0].payload;
                        return (
                          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 14px", fontSize: "0.8rem", boxShadow: "0 2px 8px rgba(0,0,0,.1)" }}>
                            <div style={{ fontWeight: 600, marginBottom: 6, color: "#111827" }}>{label}</div>
                            {payload.map((p, i) => {
                              const s = row[`${p.dataKey}__s`];
                              const c = row[`${p.dataKey}__c`];
                              const valStr = p.value != null
                                ? (hasCapacityData && c > 0 ? `${p.value}% (${s} / ${c})` : `${p.value} uds`)
                                : "Sin datos";
                              return (
                                <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                                  <span style={{ width: 10, height: 10, borderRadius: "50%", background: p.color, flexShrink: 0 }} />
                                  <span style={{ color: "#374151" }}>{p.name}:</span>
                                  <span style={{ fontWeight: 600, color: "#111827" }}>{valStr}</span>
                                </div>
                              );
                            })}
                          </div>
                        );
                      }}
                    />
                    <Legend onClick={toggleLine} wrapperStyle={{ cursor: "pointer" }} formatter={(value, entry) => <span style={{ color: hiddenLines[entry.dataKey] ? "#9ca3af" : "#374151", textDecoration: hiddenLines[entry.dataKey] ? "line-through" : "none" }}>{value}</span>} />
                    <Line type="monotone" dataKey="Total" name="Ocupación Total" stroke="#2563eb" strokeWidth={2.5} dot={false} hide={!!hiddenLines["Total"]} />
                    {hasLY && <Line type="monotone" dataKey="LY_Total" name="LY Ocupación Total" stroke="#93c5fd" strokeWidth={2} strokeDasharray="5 4" dot={false} hide={!!hiddenLines["LY_Total"]} />}
                  </LineChart>
                ) : (
                  <LineChart data={mergedDailyData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={v => hasCapacityData ? `${v}%` : v} domain={[0, "auto"]} />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (!active || !payload || !payload.length) return null;
                        const row = payload[0].payload;
                        return (
                          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 14px", fontSize: "0.8rem", boxShadow: "0 2px 8px rgba(0,0,0,.1)", maxHeight: 320, overflowY: "auto" }}>
                            <div style={{ fontWeight: 600, marginBottom: 6, color: "#111827" }}>{label}</div>
                            {payload.map((p, i) => {
                              const s = row[`${p.dataKey}__s`];
                              const c = row[`${p.dataKey}__c`];
                              const valStr = p.value != null
                                ? (hasCapacityData && c > 0 ? `${p.value}% (${s} / ${c})` : `${p.value} uds`)
                                : "Sin datos";
                              return (
                                <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                                  <span style={{ width: 10, height: 10, borderRadius: "50%", background: p.color, flexShrink: 0 }} />
                                  <span style={{ color: "#374151" }}>{p.name}:</span>
                                  <span style={{ fontWeight: 600, color: "#111827" }}>{valStr}</span>
                                </div>
                              );
                            })}
                          </div>
                        );
                      }}
                    />
                    <Legend onClick={toggleLine} wrapperStyle={{ cursor: "pointer" }} formatter={(value, entry) => <span style={{ color: hiddenLines[entry.dataKey] ? "#9ca3af" : "#374151", textDecoration: hiddenLines[entry.dataKey] ? "line-through" : "none" }}>{value}</span>} />
                    {trendGroups.map((key, i) => (
                      <Line key={key} type="monotone" dataKey={key} name={lockerTypeLabel(key)} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} hide={!!hiddenLines[key]} />
                    ))}
                    {lyTrendGroups.map((key, i) => (
                      <Line key={`LY_${key}`} type="monotone" dataKey={`LY_${key}`} name={`LY ${lockerTypeLabel(key)}`} stroke={COLORS[i % COLORS.length]} strokeWidth={1.5} strokeDasharray="5 4" dot={false} opacity={0.6} hide={!!hiddenLines[`LY_${key}`]} />
                    ))}
                  </LineChart>
                )}
              </ResponsiveContainer>
            </div>

            {/* ─── Breakdown Table ────────────────── */}
            <div style={{ background: "var(--card-bg, #fff)", borderRadius: 12, padding: 20, boxShadow: "0 1px 3px rgba(0,0,0,.08)" }}>
              <h3 style={{ fontSize: "1rem", fontWeight: 600, marginBottom: 16 }}>
                Detalle por Tipo de Locker
              </h3>
              <div style={{ overflowX: "auto" }}>
                <table className="table table-sm" style={{ fontSize: "0.85rem" }}>
                  <thead>
                    <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
                      <th style={{ textAlign: "left", padding: "8px 12px" }}>Tipo</th>
                      <th style={{ textAlign: "right", padding: "8px 12px" }}>Uds. vendidas</th>
                      <th style={{ textAlign: "right", padding: "8px 12px" }}>Revenue (€)</th>
                      <th style={{ textAlign: "right", padding: "8px 12px" }}>% del Total</th>
                      {hasLY && <th style={{ textAlign: "right", padding: "8px 12px" }}>LY Uds.</th>}
                      {hasLY && <th style={{ textAlign: "right", padding: "8px 12px" }}>Δ vs LY</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {usedTypes.map(key => {
                      const cnt = currentCounts[key] || 0;
                      const rev = currentRevenue[key] || 0;
                      const pctVal = totalSales > 0 ? ((cnt / totalSales) * 100).toFixed(1) : "0.0";
                      const lyCnt = lyCounts[key] || 0;
                      const delta = lyCnt > 0 ? ((cnt / lyCnt - 1) * 100).toFixed(1) : "—";
                      return (
                        <tr key={key} style={{ borderBottom: "1px solid #f3f4f6" }}>
                          <td style={{ padding: "8px 12px", fontWeight: 500 }}>{lockerTypeLabel(key)}</td>
                          <td style={{ textAlign: "right", padding: "8px 12px" }}>{cnt.toLocaleString()}</td>
                          <td style={{ textAlign: "right", padding: "8px 12px" }}>€{rev.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td style={{ textAlign: "right", padding: "8px 12px" }}>{pctVal}%</td>
                          {hasLY && <td style={{ textAlign: "right", padding: "8px 12px" }}>{lyCnt.toLocaleString()}</td>}
                          {hasLY && (
                            <td style={{ textAlign: "right", padding: "8px 12px", color: delta === "—" ? "#6b7280" : Number(delta) >= 0 ? "#059669" : "#dc2626" }}>
                              {delta === "—" ? "—" : `${Number(delta) >= 0 ? "+" : ""}${delta}%`}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                    {usedTypes.length === 0 && (
                      <tr>
                        <td colSpan={hasLY ? 6 : 4} style={{ textAlign: "center", padding: 24, color: "#9ca3af" }}>
                          No hay datos de tipos de locker para el período seleccionado.
                          <br /><span style={{ fontSize: "0.75rem" }}>Los datos de ItemsCounts se generan a partir de los arqueos de ventas. Si no están disponibles, regenere los arqueos.</span>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  generatedSalesArqueos: state.arqueos.generatedSalesArqueos,
  generatedOperationalArqueos: state.arqueos.generatedOperationalArqueos,
  error: state.arqueos.error,
  auth: state.auth,
  role: state.auth.role,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchPersistedSalesArqueos,
      fetchPersistedOperationalArqueos,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(OccupancyDashboardView);
